/**
 * PATH: apps/quote/src/components/builder/builder-workspace/BuilderWorkspaceProvider.tsx
 * PURPOSE: Context provider with all workspace state and actions
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { QuoteDetail } from '@/lib/quoteApi'
import type { ActiveSheet, RightDrawerTab, SaveStatus, GridViewPreset, WorkspaceSelection, WorkspaceContextValue } from './types'
import { STORAGE_KEYS, loadFromStorage, saveToStorage, loadActiveSheetForQuote, saveActiveSheetForQuote } from './storage'

export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used within a BuilderWorkspace')
  return ctx
}

export function useWorkspaceSafe() {
  return useContext(WorkspaceContext)
}

interface BuilderWorkspaceProviderProps {
  children: ReactNode
  quote: QuoteDetail | null
  isEditable: boolean
}

export function BuilderWorkspaceProvider({ children, quote, isEditable }: BuilderWorkspaceProviderProps) {
  const quoteId = quote?.id

  const [activeSheet, setActiveSheetState] = useState<ActiveSheet>(() => loadActiveSheetForQuote(quoteId, quote))
  const [leftPanelOpen, setLeftPanelOpen] = useState(() => loadFromStorage(STORAGE_KEYS.leftPanelOpen, true))
  const [leftPanelWidth, setLeftPanelWidthState] = useState(() => loadFromStorage(STORAGE_KEYS.leftPanelWidth, 280))
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false)
  const [rightDrawerTab, setRightDrawerTabState] = useState<RightDrawerTab>(() => loadFromStorage(STORAGE_KEYS.rightDrawerTab, 'inspector'))
  const [rightDrawerWidth, setRightDrawerWidthState] = useState(() => loadFromStorage(STORAGE_KEYS.rightDrawerWidth, 400))
  const [rightDrawerPinned, setRightDrawerPinned] = useState(() => loadFromStorage(STORAGE_KEYS.rightDrawerPinned, false))
  const [selection, setSelection] = useState<WorkspaceSelection>({ sectionId: null, itemId: null, cellCol: undefined })
  const [saveStatus, setSaveStatusState] = useState<SaveStatus>('idle')
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [pendingChanges, setPendingChanges] = useState(0)
  const [showKeyboardHints, setShowKeyboardHints] = useState(() => loadFromStorage(STORAGE_KEYS.showKeyboardHints, true))
  const [gridView, setGridViewState] = useState<GridViewPreset>(() => loadFromStorage(STORAGE_KEYS.gridView, 'full'))

  // Sync effects
  useEffect(() => { if (quoteId) setActiveSheetState(loadActiveSheetForQuote(quoteId, quote)) }, [quoteId])
  useEffect(() => {
    if (quote && activeSheet.type === 'section') {
      if (activeSheet.sectionId && !quote.sections?.some(s => s.id === activeSheet.sectionId)) {
        setActiveSheetState({ type: 'section', sectionId: quote.sections?.[0]?.id || null })
      } else if (!activeSheet.sectionId && quote.sections?.length) {
        setActiveSheetState({ type: 'section', sectionId: quote.sections[0].id })
      }
    }
  }, [quote?.sections?.length])
  useEffect(() => { saveActiveSheetForQuote(quoteId, activeSheet) }, [quoteId, activeSheet])

  // Persistence effects
  useEffect(() => { saveToStorage(STORAGE_KEYS.leftPanelOpen, leftPanelOpen) }, [leftPanelOpen])
  useEffect(() => { saveToStorage(STORAGE_KEYS.leftPanelWidth, leftPanelWidth) }, [leftPanelWidth])
  useEffect(() => { saveToStorage(STORAGE_KEYS.rightDrawerWidth, rightDrawerWidth) }, [rightDrawerWidth])
  useEffect(() => { saveToStorage(STORAGE_KEYS.rightDrawerPinned, rightDrawerPinned) }, [rightDrawerPinned])
  useEffect(() => { saveToStorage(STORAGE_KEYS.rightDrawerTab, rightDrawerTab) }, [rightDrawerTab])
  useEffect(() => { saveToStorage(STORAGE_KEYS.showKeyboardHints, showKeyboardHints) }, [showKeyboardHints])
  useEffect(() => { saveToStorage(STORAGE_KEYS.gridView, gridView) }, [gridView])

  // Actions
  const setActiveSheet = useCallback((sheet: ActiveSheet) => { setActiveSheetState(sheet); setSelection({ sectionId: sheet.sectionId, itemId: null, cellCol: undefined }) }, [])
  const activateSection = useCallback((sectionId: string) => { setActiveSheet({ type: 'section', sectionId }) }, [setActiveSheet])
  const activateOtherCharges = useCallback(() => { setActiveSheet({ type: 'otherCharges', sectionId: null }) }, [setActiveSheet])
  const activateAssumptions = useCallback(() => { setActiveSheet({ type: 'assumptions', sectionId: null }) }, [setActiveSheet])
  const activateSummary = useCallback(() => { setActiveSheet({ type: 'summary', sectionId: null }) }, [setActiveSheet])
  const activateTerms = useCallback(() => { setActiveSheet({ type: 'terms', sectionId: null }) }, [setActiveSheet])
  const toggleLeftPanel = useCallback(() => { setLeftPanelOpen(prev => !prev) }, [])
  const setLeftPanelWidth = useCallback((w: number) => { setLeftPanelWidthState(Math.max(200, Math.min(400, w))) }, [])
  const openRightDrawer = useCallback((tab?: RightDrawerTab) => { setRightDrawerOpen(true); if (tab) setRightDrawerTabState(tab) }, [])
  const closeRightDrawer = useCallback(() => { if (!rightDrawerPinned) setRightDrawerOpen(false) }, [rightDrawerPinned])
  const setRightDrawerTab = useCallback((tab: RightDrawerTab) => { setRightDrawerTabState(tab); setRightDrawerOpen(true) }, [])
  const setRightDrawerWidth = useCallback((w: number) => { setRightDrawerWidthState(Math.max(320, Math.min(600, w))) }, [])
  const toggleRightDrawerPin = useCallback(() => { setRightDrawerPinned(prev => !prev) }, [])
  const selectSection = useCallback((sectionId: string | null) => { setSelection({ sectionId, itemId: null, cellCol: undefined }) }, [])
  const selectItem = useCallback((sectionId: string, itemId: string | null, cellCol?: number) => { setSelection({ sectionId, itemId, cellCol }); if (itemId) openRightDrawer('inspector') }, [openRightDrawer])
  const clearSelection = useCallback(() => { setSelection({ sectionId: null, itemId: null, cellCol: undefined }) }, [])
  const setSaveStatus = useCallback((status: SaveStatus) => { setSaveStatusState(status) }, [])
  const markSaved = useCallback(() => { setSaveStatusState('saved'); setLastSaved(new Date()); setPendingChanges(0) }, [])
  const incrementPendingChanges = useCallback(() => { setPendingChanges(prev => prev + 1); setSaveStatusState('saving') }, [])
  const toggleKeyboardHints = useCallback(() => { setShowKeyboardHints(prev => !prev) }, [])
  const setGridView = useCallback((preset: GridViewPreset) => { setGridViewState(preset) }, [])
  const toggleGridView = useCallback(() => { setGridViewState(prev => (prev === 'compact' ? 'full' : 'compact')) }, [])

  const contextValue: WorkspaceContextValue = {
    activeSheet, leftPanelOpen, leftPanelWidth, rightDrawerOpen, rightDrawerTab, rightDrawerWidth, rightDrawerPinned,
    selection, saveStatus, lastSaved, pendingChanges, showKeyboardHints, gridView, quote, isEditable,
    setActiveSheet, activateSection, activateOtherCharges, activateAssumptions, activateSummary, activateTerms,
    toggleLeftPanel, setLeftPanelWidth, openRightDrawer, closeRightDrawer, setRightDrawerTab, setRightDrawerWidth,
    toggleRightDrawerPin, selectSection, selectItem, clearSelection, setSaveStatus, markSaved, incrementPendingChanges,
    toggleKeyboardHints, setGridView, toggleGridView,
  }

  return <WorkspaceContext.Provider value={contextValue}>{children}</WorkspaceContext.Provider>
}
